import { useState, useRef, useEffect } from 'react'
import { useWindowStore } from '../../stores/windowStore'
import './Terminal.css'

interface HistoryEntry {
  type: 'input' | 'output'
  content: string
}

// Virtual file system matching FileManager
const fileSystem: Record<string, { name: string; type: 'folder' | 'file'; content?: string }[]> = {
  '/': [
    { name: 'DOCUMENTS', type: 'folder' },
    { name: 'PROGRAMS', type: 'folder' },
    { name: 'GAMES', type: 'folder' },
    { name: 'SYSTEM', type: 'folder' },
    { name: 'README.TXT', type: 'file', content: 'Welcome to PersonaOS v1.0' },
  ],
  '/DOCUMENTS': [
    { name: 'about.txt', type: 'file', content: 'Persona Science - Persistent Identity Intelligence' },
    { name: 'solutions.txt', type: 'file', content: 'Multi-Domain Fusion | Edge Devices | Persona Analytics' },
    { name: 'contact.txt', type: 'file', content: 'Email: contact@personascience.com' },
    { name: 'mission.txt', type: 'file', content: 'To fuse multi-signal and side-channel data into unified identity intelligence.' },
  ],
  '/PROGRAMS': [
    { name: 'calculator.exe', type: 'file' },
    { name: 'notepad.exe', type: 'file' },
    { name: 'terminal.exe', type: 'file' },
  ],
  '/GAMES': [
    { name: 'minesweeper.exe', type: 'file' },
    { name: 'oregon.exe', type: 'file' },
    { name: 'tradewars.exe', type: 'file' },
    { name: 'highscores.dat', type: 'file', content: '1. AAA - 999\n2. BBB - 500\n3. CCC - 250' },
  ],
  '/SYSTEM': [
    { name: 'config.sys', type: 'file', content: 'DEVICE=PERSONA.SYS\nFILES=40\nBUFFERS=20' },
    { name: 'autoexec.bat', type: 'file', content: '@ECHO OFF\nPATH C:\\PROGRAMS;C:\\GAMES\nPROMPT $P$G' },
    { name: 'persona.ini', type: 'file', content: '[Settings]\nTheme=amber\nScanlines=on' },
  ],
}

// Map executables to app IDs
const exeToApp: Record<string, { appId: string; title: string; icon: string }> = {
  'calculator': { appId: 'calculator', title: 'Calculator', icon: 'calculator' },
  'calculator.exe': { appId: 'calculator', title: 'Calculator', icon: 'calculator' },
  'notepad': { appId: 'notepad', title: 'Notepad', icon: 'file-text' },
  'notepad.exe': { appId: 'notepad', title: 'Notepad', icon: 'file-text' },
  'terminal': { appId: 'terminal', title: 'Terminal', icon: 'terminal' },
  'terminal.exe': { appId: 'terminal', title: 'Terminal', icon: 'terminal' },
  'minesweeper': { appId: 'minesweeper', title: 'Minesweeper', icon: 'bomb' },
  'minesweeper.exe': { appId: 'minesweeper', title: 'Minesweeper', icon: 'bomb' },
  'oregon': { appId: 'oregon-trail', title: 'Oregon Trail', icon: 'terminal' },
  'oregon.exe': { appId: 'oregon-trail', title: 'Oregon Trail', icon: 'terminal' },
  'tradewars': { appId: 'tradewars', title: 'TradeWars 2002', icon: 'rocket' },
  'tradewars.exe': { appId: 'tradewars', title: 'TradeWars 2002', icon: 'rocket' },
  'lord': { appId: 'lord', title: 'Legend of the Red Dragon', icon: 'dragon' },
  'lord.exe': { appId: 'lord', title: 'Legend of the Red Dragon', icon: 'dragon' },
}

interface CommandContext {
  openWindow: (appId: string, title: string, icon: string) => void
  currentPath: string
  setCurrentPath: (path: string) => void
}

// Helper to resolve path (handles .. and relative paths)
function resolvePath(currentPath: string, targetPath: string): string {
  // Absolute path
  if (targetPath.startsWith('/')) {
    return targetPath.toUpperCase()
  }

  // Home directory
  if (targetPath === '~' || targetPath === '') {
    return '/'
  }

  // Parent directory
  if (targetPath === '..') {
    const parts = currentPath.split('/').filter(Boolean)
    parts.pop()
    return '/' + parts.join('/')
  }

  // Relative path
  const resolved = currentPath === '/' ? `/${targetPath}` : `${currentPath}/${targetPath}`
  return resolved.toUpperCase()
}

// Helper to format path as DOS-style
function formatDosPath(path: string): string {
  if (path === '/') return 'C:\\'
  return 'C:' + path.replace(/\//g, '\\')
}

// Helper to check if path is a directory
function isDirectory(path: string): boolean {
  return path in fileSystem
}

// Helper to find file in current directory or by path
function findFile(currentPath: string, filename: string): { name: string; type: 'folder' | 'file'; content?: string } | null {
  const files = fileSystem[currentPath]
  if (!files) return null
  return files.find(f => f.name.toLowerCase() === filename.toLowerCase()) || null
}

const COMMANDS: Record<string, (args: string[], context: CommandContext) => string> = {
  help: () => `
Available commands:
  help           Show this help message
  cd [dir]       Change directory
  pwd            Print working directory
  dir / ls       List directory contents
  cat [file]     Display file contents
  type [file]    Display file contents (DOS-style)
  cls / clear    Clear the terminal
  echo [text]    Echo text back
  whoami         Display current user
  uname          System information
  date           Show current date/time
  ver / version  Show version info
  about          About Persona Science
  contact        Open contact form
  solutions      View solutions

You can also run programs by typing their name:
  calculator     Run Calculator
  notepad        Run Notepad
  minesweeper    Run Minesweeper
  oregon         Run Oregon Trail
`,

  cd: (args, ctx) => {
    const target = args[0] || '/'
    const newPath = resolvePath(ctx.currentPath, target)

    if (!isDirectory(newPath) && newPath !== '/') {
      // Check if it's an empty path (going above root)
      if (newPath === '') {
        ctx.setCurrentPath('/')
        return ''
      }
      return `The system cannot find the path specified: ${target}`
    }

    ctx.setCurrentPath(newPath || '/')
    return ''
  },

  pwd: (_, ctx) => formatDosPath(ctx.currentPath),

  dir: (args, ctx) => {
    const targetPath = args[0] ? resolvePath(ctx.currentPath, args[0]) : ctx.currentPath
    const files = fileSystem[targetPath]

    if (!files) {
      return `Directory not found: ${args[0] || targetPath}`
    }

    const now = new Date()
    const dateStr = `${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}-${String(now.getFullYear()).slice(-2)}`
    const hour = now.getHours()
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
    const timeStr = `${String(hour12).padStart(2, ' ')}:${String(now.getMinutes()).padStart(2, '0')}${hour >= 12 ? 'p' : 'a'}`

    let output = ` Volume in drive C is PERSONAOS\n`
    output += ` Volume Serial Number is 1337-0DAY\n`
    output += ` Directory of ${formatDosPath(targetPath)}\n\n`

    // Add . and .. entries
    output += `.               <DIR>     ${dateStr}  ${timeStr}\n`
    output += `..              <DIR>     ${dateStr}  ${timeStr}\n`

    // List files with proper 9.3 format alignment (cheating from 8.3 to fit DOCUMENTS)
    let totalSize = 0
    let fileCount = 0
    let dirCount = 2 // . and ..

    files.forEach(f => {
      if (f.type === 'folder') {
        const name = f.name.substring(0, 9).toUpperCase().padEnd(9, ' ')
        output += `${name}      <DIR>     ${dateStr}  ${timeStr}\n`
        dirCount++
      } else {
        const parts = f.name.toUpperCase().split('.')
        const name = (parts[0] ?? '').substring(0, 9).padEnd(9, ' ')
        const ext = (parts[1] ?? '').substring(0, 3).padEnd(3, ' ')
        const size = (f.content?.length || 0)
        totalSize += size
        fileCount++
        output += `${name} ${ext}   ${String(size).padStart(8, ' ')}  ${dateStr}  ${timeStr}\n`
      }
    })

    output += `         ${fileCount} file(s)    ${String(totalSize).padStart(10, ' ')} bytes\n`
    output += `         ${dirCount} dir(s)     1,234,567 bytes free`

    return output
  },

  ls: (args, ctx) => COMMANDS.dir!(args, ctx),

  cat: (args, ctx) => {
    if (!args[0]) {
      return 'Usage: cat <filename>'
    }

    const filename = args[0]

    // Check if it's a path
    if (filename.includes('/')) {
      const parts = filename.split('/')
      const name = parts.pop() || ''
      const dirPath = resolvePath(ctx.currentPath, parts.join('/'))
      const file = findFile(dirPath, name)
      if (file && file.content) return file.content
      return `File not found: ${filename}`
    }

    // Check current directory
    const file = findFile(ctx.currentPath, filename)
    if (file) {
      if (file.type === 'folder') {
        return `${filename}: Is a directory`
      }
      if (file.content) {
        return file.content
      }
      return `${filename}: Binary file (cannot display)`
    }

    return `File not found: ${filename}`
  },

  type: (args, ctx) => COMMANDS.cat!(args, ctx),

  clear: () => '__CLEAR__',
  cls: () => '__CLEAR__',

  date: () => new Date().toString(),

  echo: (args) => args.join(' ') || '',

  whoami: () => 'guest@persona-os',

  uname: () => `PersonaOS 1.0.0 (x86_64-persona-personaos)`,

  ver: () => `PersonaOS Terminal v1.0.0
Built with React + TypeScript
© 2025 Persona Science`,

  version: () => COMMANDS.ver!([], {} as CommandContext),

  about: () => `
PERSONA SCIENCE
===============
Persistent Identity Intelligence

Multi-domain fusion platform for defense and security.
Combining biometrics, RF signals, and browser fingerprinting
for real-time threat detection.

© 2025 Persona Science
`,

  contact: (_, ctx) => {
    ctx.openWindow('contact', 'Contact', 'mail')
    return 'Opening contact form...'
  },

  solutions: (_, ctx) => {
    ctx.openWindow('products', 'Solutions', 'cube')
    return 'Opening solutions catalog...'
  },
}

export default function Terminal() {
  const [currentPath, setCurrentPath] = useState('/')
  const [history, setHistory] = useState<HistoryEntry[]>([
    {
      type: 'output',
      content: `PersonaOS Terminal v1.0
Type 'help' for available commands.
`,
    },
  ])
  const [input, setInput] = useState('')
  const [commandHistory, setCommandHistory] = useState<string[]>([])
  const [historyIndex, setHistoryIndex] = useState(-1)

  const inputRef = useRef<HTMLInputElement>(null)
  const terminalRef = useRef<HTMLDivElement>(null)

  const openWindow = useWindowStore((state) => state.openWindow)

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  useEffect(() => {
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight
    }
  }, [history])

  const executeCommand = (cmd: string) => {
    const trimmed = cmd.trim()
    if (!trimmed) return

    const parts = trimmed.split(/\s+/)
    const command = parts[0]?.toLowerCase() || ''
    const args = parts.slice(1)

    const context: CommandContext = {
      openWindow,
      currentPath,
      setCurrentPath,
    }

    let output: string

    // First check if it's a built-in command
    if (command in COMMANDS) {
      output = COMMANDS[command]!(args, context)
    }
    // Then check if it's an executable
    else if (command in exeToApp) {
      const app = exeToApp[command]!
      openWindow(app.appId, app.title, app.icon)
      output = `Launching ${app.title}...`
    }
    // Check if it exists in current directory as an exe
    else {
      const file = findFile(currentPath, command) || findFile(currentPath, command + '.exe')
      if (file && (file.name.endsWith('.exe') || command in exeToApp)) {
        const exeName = file.name.toLowerCase()
        const baseName = exeName.replace('.exe', '')
        if (baseName in exeToApp || exeName in exeToApp) {
          const app = exeToApp[baseName] ?? exeToApp[exeName]
          if (app) {
            openWindow(app.appId, app.title, app.icon)
            output = `Launching ${app.title}...`
          } else {
            output = `'${command}' is not a recognized executable.`
          }
        } else {
          output = `'${command}' is not a recognized executable.`
        }
      } else {
        output = `'${command}' is not recognized as an internal or external command,\noperable program or batch file.`
      }
    }

    if (output === '__CLEAR__') {
      setHistory([])
    } else {
      const prompt = `${formatDosPath(currentPath)}> ${trimmed}`
      setHistory((prev) => [
        ...prev,
        { type: 'input', content: prompt },
        ...(output ? [{ type: 'output' as const, content: output }] : []),
      ])
    }

    setCommandHistory((prev) => [...prev, trimmed])
    setHistoryIndex(-1)
    setInput('')
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      executeCommand(input)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (commandHistory.length > 0) {
        const newIndex =
          historyIndex === -1
            ? commandHistory.length - 1
            : Math.max(0, historyIndex - 1)
        setHistoryIndex(newIndex)
        setInput(commandHistory[newIndex] || '')
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (historyIndex !== -1) {
        const newIndex = historyIndex + 1
        if (newIndex >= commandHistory.length) {
          setHistoryIndex(-1)
          setInput('')
        } else {
          setHistoryIndex(newIndex)
          setInput(commandHistory[newIndex] || '')
        }
      }
    } else if (e.key === 'Tab') {
      e.preventDefault()
      // Simple tab completion for files in current directory
      const files = fileSystem[currentPath] ?? []
      const matches = files.filter(f =>
        f.name.toLowerCase().startsWith(input.toLowerCase())
      )
      if (matches.length === 1 && matches[0]) {
        setInput(matches[0].name)
      }
    }
  }

  const handleTerminalClick = () => {
    inputRef.current?.focus()
  }

  return (
    <div className="terminal-app" onClick={handleTerminalClick} ref={terminalRef}>
      <div className="terminal-output">
        {history.map((entry, index) => (
          <pre key={index} className={`terminal-line terminal-${entry.type}`}>
            {entry.content}
          </pre>
        ))}
      </div>

      <div className="terminal-input-line">
        <span className="terminal-prompt">{formatDosPath(currentPath)}&gt; </span>
        <input
          ref={inputRef}
          type="text"
          className="terminal-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          spellCheck={false}
          autoComplete="off"
        />
      </div>
    </div>
  )
}
